/**
 * Kontakthistorie eines Kunden.
 *
 * Ersetzt das eine Bemerkungsfeld, das es vorher gab. Wer im Januar
 * telefoniert hat und im März nachfragt, überschrieb damit, was im Januar
 * besprochen war.
 *
 * Der Zeitpunkt ist voreingestellt auf jetzt und lässt sich zurückdatieren:
 * notiert wird meist nach dem Gespräch, manchmal Tage später.
 */

import { useState } from 'react'
import {
  KONTAKTARTTEXT,
  useKontaktAnlegen,
  useKontaktLoeschen,
  useKontakte,
  type Kontaktart,
} from '../api/kundenakte'
import { Fehlerhinweis, Ladeanzeige } from '../bausteine/Hinweise'
import { darfSchreiben, useKonto } from '../konto/KontoKontext'

const ARTEN = Object.keys(KONTAKTARTTEXT) as Kontaktart[]

/** Datum und Uhrzeit für ein datetime-local-Feld. */
function jetztFuerFeld(): string {
  const jetzt = new Date()
  const versetzt = new Date(jetzt.getTime() - jetzt.getTimezoneOffset() * 60000)
  return versetzt.toISOString().slice(0, 16)
}

function zeitpunktDeutsch(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function Kontakte({ kundeId }: { kundeId: string }) {
  const { konto } = useKonto()
  const liste = useKontakte(kundeId)
  const anlegen = useKontaktAnlegen(kundeId)
  const loeschen = useKontaktLoeschen(kundeId)
  const [offen, setzeOffen] = useState(false)
  const [art, setzeArt] = useState<Kontaktart>('telefon')
  const [zeitpunkt, setzeZeitpunkt] = useState(jetztFuerFeld)
  const [betreff, setzeBetreff] = useState('')
  const [partner, setzePartner] = useState('')
  const [inhalt, setzeInhalt] = useState('')

  const schreiben = darfSchreiben(konto)

  async function speichern(ereignis: React.FormEvent) {
    ereignis.preventDefault()
    await anlegen.mutateAsync({
      art,
      // Ohne Zeitzone würde der Server die Angabe als UTC lesen und ein
      // Gespräch vom Nachmittag zwei Stunden früher einsortieren.
      zeitpunkt: new Date(zeitpunkt).toISOString(),
      betreff,
      gespraechspartner: partner || null,
      inhalt: inhalt || null,
    })
    setzeBetreff('')
    setzePartner('')
    setzeInhalt('')
    setzeZeitpunkt(jetztFuerFeld())
    setzeOffen(false)
  }

  if (liste.isLoading) return <Ladeanzeige />

  return (
    <div className="space-y-4">
      <Fehlerhinweis fehler={liste.error ?? anlegen.error ?? loeschen.error} />

      {schreiben &&
        (offen ? (
          <form onSubmit={(e) => void speichern(e)} className="karte space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="kontaktart">Weg</label>
                <select
                  id="kontaktart"
                  value={art}
                  onChange={(e) => setzeArt(e.target.value as Kontaktart)}
                >
                  {ARTEN.map((a) => (
                    <option key={a} value={a}>
                      {KONTAKTARTTEXT[a]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="zeitpunkt">Wann</label>
                <input
                  id="zeitpunkt"
                  type="datetime-local"
                  value={zeitpunkt}
                  onChange={(e) => setzeZeitpunkt(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="betreff">Worum ging es</label>
              <input
                id="betreff"
                value={betreff}
                onChange={(e) => setzeBetreff(e.target.value)}
                placeholder="Termin Herbstprüfung"
                required
                maxLength={200}
              />
            </div>

            <div>
              <label htmlFor="partner">Mit wem</label>
              <input
                id="partner"
                value={partner}
                onChange={(e) => setzePartner(e.target.value)}
                placeholder="Herr Janssen, Bauhof"
                maxLength={120}
              />
            </div>

            <div>
              <label htmlFor="inhalt">Notiz</label>
              <textarea
                id="inhalt"
                value={inhalt}
                onChange={(e) => setzeInhalt(e.target.value)}
                placeholder="Bauhof meldet sich wegen der Ferien noch einmal."
              />
            </div>

            <div className="flex gap-2">
              <button type="submit" className="knopf-haupt" disabled={anlegen.isPending}>
                {anlegen.isPending ? 'Speichert …' : 'Eintragen'}
              </button>
              <button type="button" className="knopf-still" onClick={() => setzeOffen(false)}>
                Abbrechen
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="knopf-haupt" onClick={() => setzeOffen(true)}>
            Kontakt notieren
          </button>
        ))}

      {liste.data?.length === 0 ? (
        <p className="text-slate-600">Noch nichts notiert.</p>
      ) : (
        <ol className="space-y-2">
          {liste.data?.map((k) => (
            <li key={k.id} className="karte">
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="marke bg-akzent-grund text-akzent-700">
                  {KONTAKTARTTEXT[k.art]}
                </span>
                <span className="font-semibold">{k.betreff}</span>
                <span className="text-base text-slate-600">{zeitpunktDeutsch(k.zeitpunkt)}</span>
              </div>
              {k.gespraechspartner && (
                <p className="mt-1 text-base text-slate-700">mit {k.gespraechspartner}</p>
              )}
              {k.inhalt && <p className="mt-2 whitespace-pre-wrap">{k.inhalt}</p>}
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-base text-slate-500">
                  {k.verfasser ? `notiert von ${k.verfasser}` : ''}
                </span>
                {schreiben && (
                  <button
                    type="button"
                    className="text-base text-slate-600 underline"
                    onClick={() => loeschen.mutate(k.id)}
                  >
                    Löschen
                  </button>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
